import React from 'react';
import Image from 'frontastic/lib/image';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Reference } from 'types/reference';
import dynamic from 'next/dynamic';
import { StaticImageData } from 'next/image';
import { usePathname } from 'next/navigation';
import Head from 'next/head';
import { NationalSemibold, Tiempos } from 'fonts';

const Markdown = dynamic(() => import('../../organisms/markdown'));

export type InfoContentProps = {
  data?: any;
  index?: number;
  totalSections?: number;
};

type ctaButtonType = {
  href: string | Reference | undefined;
  title: string | undefined;
};

type contentType = { icon: { url: string | StaticImageData | undefined; title: string | undefined }; content: string };

const InfoContent = ({ data, index, totalSections }: React.PropsWithChildren<InfoContentProps>) => {
  const currentURL = usePathname();
  const isExperienceCenterPage = currentURL.includes('/experience-centres/');

  return (
    <div
      className={`flex items-center gap-24 lg:gap-0 ${
        data?.desktop_image_alignment === 'Left' ? 'flex-col lg:flex-row' : 'lg:flex-row-reverse'
      } ${data?.mobile_image_alignment === 'Top' ? 'flex-col' : 'flex-col-reverse'}`}
    >
      <Head>
        <link rel="preload" href={`${data?.image?.url}?format=webp`} as="image" />
        <link rel="dns-prefetch" href="https://images.contentstack.io" />
        <link rel="preconnect" href="https://images.contentstack.io" />
      </Head>

      <div className="relative w-full lg:flex-1 lg:flex-grow">
        <picture>
          <source media="(max-width:699px)" srcSet={`${data?.image?.url}?format=webp`} />
          <source media="(min-width:700px)" srcSet={`${data?.image?.url}?w=1024&format=webp 1024w`} />
          <Image
            loading="eager"
            src={`${data?.image?.url}?format=webp`}
            alt={data?.image?.description}
            sizes="100vw"
            style={{ width: '100%', objectFit: 'cover' }}
            width={500}
            height={300}
            className={`${isExperienceCenterPage ? 'lg:h-[50rem] xl:h-[55rem] 2xl:h-[45rem]' : ''}`}
          />
        </picture>
      </div>

      <div className="flex-1 flex-grow">
        <div
          className={`px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80 ${
            data?.desktop_image_alignment === 'Left' ? 'px-24 md:pr-0 2xl:pr-0' : ' px-24 2xl:pl-0'
          }`}
        >
          <Typography as="h1" className={`pb-20 text-32 font-medium leading-10 text-[#090909] xl:text-28 2xl:text-32 ${Tiempos.className}`}>
            {data?.title}
          </Typography>

          <Markdown
            markdown={data?.description}
            className={`flex flex-col gap-16 text-[1rem] font-normal text-[#333] xl:text-14 2xl:text-[1rem] ${
              index === 0 || (totalSections && index === totalSections - 1)
                ? 'leading-8 lg:text-[1.25rem]'
                : 'leading-6'
            }`}
          />

          <div className="mt-16 grid grid-cols-1 gap-x-20 gap-y-20 2xl:grid-cols-2">
            {data?.content_with_icon &&
              data?.content_with_icon.map((content: contentType, index: number) => (
                <div className="flex items-center gap-16" key={index}>
                  <div className="relative h-24 w-24">
                    <Image
                      loading="lazy"
                      src={`${content?.icon?.url}?format=webp`}
                      alt={content?.icon?.title}
                      fill
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <Markdown markdown={content?.content} className="max-w-[13rem]" />
                </div>
              ))}
          </div>

          <div className="mt-22 mb-10 flex flex-col gap-10 md:mt-48 md:flex-row">
            {data?.cta?.length > 0 &&
              data?.cta?.map((ctaButton: any, index: number) => (
                <div className="" key={index}>
                  <Link
                    target={ctaButton?.button?.href?.includes('https://') ? '_blank' : ''}
                    rel="noopener noreferrer"
                    link={ctaButton?.button?.href}
                  >
                    <Button
                      className={
                        index === 0
                          ? 'border-1 w-full rounded-none border border-[#2B2C39] bg-white px-18 py-10 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit md:px-32 md:py-18'
                          : 'border-1 w-full rounded-none border border-[#2B2C39] bg-[#2B2C39] px-18 py-10 shadow-sm hover:!bg-[#394A54] md:w-fit md:px-32 md:py-18'
                      }
                    >
                      <Typography
                        as="span"
                        className={`text-16 font-medium leading-6 xl:text-14 2xl:text-16  ${NationalSemibold.className} ${
                          index === 0 ? 'text-[#090909]' : 'text-white'
                        }`}
                      >
                        {ctaButton?.button?.title}
                      </Typography>
                    </Button>
                  </Link>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoContent;
